import React, { useState } from 'react';
import Modal from 'react-modal';

function FileView({ files, onFileUpload, onFileDelete, onFileCopy, onFileDownload, onFileUpdate }) {
  const [viewMode, setViewMode] = useState('grid');
  const [file, setFile] = useState(null);
  
  // Sharing State
  const [sharingMode, setSharingMode] = useState('private'); // private, public, users, all_users
  const [sharedWith, setSharedWith] = useState('');
  
  // Expiration State
  const [expirationMode, setExpirationMode] = useState('never'); // never, 10m, 1h, 1d, 1w, custom
  const [customExpiration, setCustomExpiration] = useState('');

  // Edit State
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingFile, setEditingFile] = useState(null);
  const [editSharingMode, setEditSharingMode] = useState('private');
  const [editSharedWith, setEditSharedWith] = useState('');
  const [editExpiresAt, setEditExpiresAt] = useState('');

  const handleUpload = (e) => {
    e.preventDefault();
    if (!file) return;
    
    let expiresAt = null;
    const now = new Date();
    
    switch (expirationMode) {
      case '10m':
        now.setMinutes(now.getMinutes() + 10);
        expiresAt = now.toISOString();
        break;
      case '1h':
        now.setHours(now.getHours() + 1);
        expiresAt = now.toISOString();
        break;
      case '1d':
        now.setDate(now.getDate() + 1);
        expiresAt = now.toISOString();
        break;
      case '1w':
        now.setDate(now.getDate() + 7);
        expiresAt = now.toISOString();
        break;
      case 'custom':
        if (customExpiration) {
          expiresAt = new Date(customExpiration).toISOString();
        }
        break;
      default:
        expiresAt = null;
    }
    
    let isPublic = false;
    let finalSharedWith = '';
    
    if (sharingMode === 'public') {
      isPublic = true;
    } else if (sharingMode === 'users') {
      finalSharedWith = sharedWith;
    } else if (sharingMode === 'all_users') {
      finalSharedWith = '__ALL_USERS__';
    }
    
    onFileUpload(file, { is_public: isPublic, expires_at: expiresAt, shared_with: finalSharedWith });
    
    // Reset form
    setFile(null);
    setSharingMode('private');
    setSharedWith('');
    setExpirationMode('never');
    setCustomExpiration('');
    e.target.reset();
  };

  const openEditModal = (f) => {
    setEditingFile(f);
    
    // Determine sharing mode
    if (f.is_public) {
      setEditSharingMode('public');
      setEditSharedWith('');
    } else if (f.shared_with && f.shared_with.includes('__ALL_USERS__')) {
      setEditSharingMode('all_users');
      setEditSharedWith('');
    } else if (f.shared_with && f.shared_with.length > 0) {
      setEditSharingMode('users');
      setEditSharedWith(f.shared_with.join(', '));
    } else {
      setEditSharingMode('private');
      setEditSharedWith('');
    }

    setEditExpiresAt(f.expires_at ? new Date(f.expires_at).toISOString().slice(0, 16) : '');
    setEditModalOpen(true);
  };

  const handleUpdate = () => {
    if (!editingFile) return;
    
    let isPublic = false;
    let finalSharedWith = '';
    
    if (editSharingMode === 'public') {
      isPublic = true;
    } else if (editSharingMode === 'users') {
      finalSharedWith = editSharedWith;
    } else if (editSharingMode === 'all_users') {
      finalSharedWith = '__ALL_USERS__';
    }

    onFileUpdate(editingFile.id, {
      is_public: isPublic,
      shared_with: finalSharedWith,
      expires_at: editExpiresAt || ''
    });
    setEditModalOpen(false);
  };

  return (
    <div>
      <div className="flex-between mb-4">
        <h2 className="mt-0 mb-0">Files</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button 
            onClick={() => setViewMode('grid')} 
            className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Grid
          </button>
          <button 
            onClick={() => setViewMode('list')} 
            className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-secondary'}`}
          >
            List
          </button>
        </div>
      </div>
      <div className="card mb-4">
        <form onSubmit={handleUpload} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <input type="file" onChange={e => setFile(e.target.files[0])} className="input" style={{ width: 'auto', flex: 1 }} />
            <button type="submit" className="btn btn-primary" disabled={!file}>Upload File</button>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            {/* Sharing Control */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label style={{ fontWeight: 600, fontSize: '0.9em' }}>Sharing</label>
              <select 
                value={sharingMode} 
                onChange={e => setSharingMode(e.target.value)} 
                className="input"
              >
                <option value="private">Private (Only Me)</option>
                <option value="public">Public (Anyone with Link)</option>
                <option value="users">Specific Users</option>
                <option value="all_users">All Authenticated Users</option>
              </select>
              {sharingMode === 'users' && (
                <input 
                  type="text" 
                  value={sharedWith} 
                  onChange={e => setSharedWith(e.target.value)} 
                  className="input"
                  placeholder="Usernames/Emails (comma separated)"
                />
              )}
            </div>

            {/* Expiration Control */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label style={{ fontWeight: 600, fontSize: '0.9em' }}>Expiration</label>
              <select 
                value={expirationMode} 
                onChange={e => setExpirationMode(e.target.value)} 
                className="input"
              >
                <option value="never">Never</option>
                <option value="10m">10 Minutes</option>
                <option value="1h">1 Hour</option>
                <option value="1d">1 Day</option>
                <option value="1w">1 Week</option>
                <option value="custom">Custom Date/Time</option>
              </select>
              {expirationMode === 'custom' && (
                <input 
                  type="datetime-local" 
                  value={customExpiration} 
                  onChange={e => setCustomExpiration(e.target.value)} 
                  className="input"
                />
              )}
            </div>
          </div>
        </form>
      </div>
      <div className={viewMode === 'grid' ? 'view-grid' : 'view-list'}>
        {files.length === 0 && <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>No files found.</div>}
        {files.map(f => {
          let downloadUrl;
          if (f.is_public) {
            downloadUrl = `${window.location.origin}/file/download/${f.id}/${encodeURIComponent(f.filename || f.name)}`;
          } else {
            downloadUrl = `${window.location.origin}/s/file/${f.id}/${encodeURIComponent(f.filename || f.name)}`;
          }
          
          return (
            <div key={f.id} className="view-item">
              <div className="view-item-icon">📄</div>
              <div className="view-item-details">
                <div className="view-item-name" title={f.filename || f.name}>{f.filename || f.name}</div>
                <div className="view-item-id">ID: {f.id}</div>
                <div style={{ fontSize: '0.8em', color: 'var(--text-secondary)', marginTop: 4 }}>
                  {f.is_public ? '🌐 Public' : (f.shared_with && f.shared_with.length > 0 ? '👥 Shared' : '🔒 Private')}
                  {f.expires_at && ` • Expires: ${new Date(f.expires_at).toLocaleDateString()}`}
                </div>
              </div>
              <div className="view-item-actions">
                <button onClick={() => onFileCopy(downloadUrl)} title="Copy Download URL" className="btn-icon">📋</button>
                <button onClick={() => onFileDownload(f.id, f.filename || f.name)} title="Download" className="btn-icon">⬇️</button>
                {f.is_owner && (
                  <>
                    <button onClick={() => openEditModal(f)} title="Edit" className="btn-icon">✏️</button>
                    <button onClick={() => onFileDelete(f.id)} title="Delete" className="btn-icon" style={{ color: 'var(--danger)' }}>🗑️</button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Modal
        isOpen={editModalOpen}
        onRequestClose={() => setEditModalOpen(false)}
        contentLabel="Edit File"
        style={{ content: { maxWidth: 500, margin: 'auto', borderRadius: 8, padding: 24, height: 'fit-content' } }}
      >
        <h2 style={{ marginTop: 0 }}>Edit File</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label style={{ fontWeight: 600, fontSize: '0.9em', display: 'block', marginBottom: '0.5rem' }}>Sharing</label>
            <select 
              value={editSharingMode} 
              onChange={e => setEditSharingMode(e.target.value)} 
              className="input"
            >
              <option value="private">Private (Only Me)</option>
              <option value="public">Public (Anyone with Link)</option>
              <option value="users">Specific Users</option>
              <option value="all_users">All Authenticated Users</option>
            </select>
            {editSharingMode === 'users' && (
              <input 
                type="text" 
                value={editSharedWith} 
                onChange={e => setEditSharedWith(e.target.value)} 
                className="input"
                placeholder="Usernames/Emails (comma separated)"
                style={{ marginTop: '0.5rem' }}
              />
            )}
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>Expires At</label>
            <input 
              type="datetime-local" 
              value={editExpiresAt} 
              onChange={e => setEditExpiresAt(e.target.value)} 
              className="input"
            />
            <small style={{ color: 'var(--text-secondary)' }}>Leave empty for no expiration</small>
          </div>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <button onClick={() => setEditModalOpen(false)} className="btn btn-secondary">Cancel</button>
            <button onClick={handleUpdate} className="btn btn-primary">Save Changes</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default FileView;

